/**
 * Driverning hozirgi mahalliy vaqti (shtat bo'yicha) va qo'ng'iroq vaqti holati; har daqiqada o'zi yangilanadi
 * (karta va sahifaning qolgani qayta chizilmaydi). Shtat bo'lmasa -- hech narsa (taxminiy vaqt chalg'itmasin).
 */
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Clock3 } from "lucide-react";
import { cn, dayjs } from "@/lib/utils";
import { useNow } from "@/lib/useNow";
import { CALL_HOURS, callWindow, driverTz, tzAbbr } from "./timezones";

const hh = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;

function duration(minutes: number, t: TFunction): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? t("localTime.hm", { h, m }) : t("localTime.m", { m });
}

/** Ixcham (pipeline kartasi): "6:05 CDT", qo'ng'iroq vaqti ichida -- yashil. `detail` -- profil: holat bilan */
export function DriverLocalTime({ state, detail, className }: { state: string; detail?: boolean; className?: string }) {
  const { t } = useTranslation();
  const now = useNow("minute");
  if (!state) return null;
  const tz = driverTz(state);
  const time = `${dayjs(now).tz(tz).format("H:mm")} ${tzAbbr(tz, now)}`;
  const call = callWindow(now, tz);
  const title = t(call.open ? "localTime.titleOpen" : "localTime.titleClosed", { from: hh(CALL_HOURS[0]), to: hh(CALL_HOURS[1]) });
  if (!detail) {
    return (
      <span className={cn(call.open && "font-semibold text-success", className)} title={title}>
        {time}
      </span>
    );
  }
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-1", className)} title={title}>
      <Clock3 size={13} className="shrink-0" aria-hidden />
      {t("localTime.label", { time })} ·
      {call.open ? <span className="font-semibold text-success">{t("localTime.open", { left: duration(call.minutes, t) })}</span> : <span>{t("localTime.opensIn", { wait: duration(call.minutes, t) })}</span>}
    </span>
  );
}

/** Profil sarlavhasi (oldingi reja): katta yupqa raqamlar, yonida AM/PM va mintaqa, pastida qo'ng'iroq vaqti holati */
export function DriverLocalClock({ state, className }: { state: string; className?: string }) {
  const { t } = useTranslation();
  const now = useNow("minute");
  if (!state) return null;
  const tz = driverTz(state);
  const local = dayjs(now).tz(tz);
  const call = callWindow(now, tz);
  const title = t(call.open ? "localTime.titleOpen" : "localTime.titleClosed", { from: hh(CALL_HOURS[0]), to: hh(CALL_HOURS[1]) });
  return (
    <div className={cn("flex items-end gap-3", className)} title={title}>
      <span className="tabular text-[56px] leading-[0.9] font-extralight tracking-[-0.04em]">{local.format("h:mm")}</span>
      <span className="flex min-w-0 flex-col gap-0.5 pb-1">
        <span className="text-[15px] font-semibold">
          {local.format("A")} {tzAbbr(tz, now)}
        </span>
        <span className={cn("text-[13px]", call.open ? "font-semibold text-success" : "text-muted")}>
          {call.open ? t("localTime.open", { left: duration(call.minutes, t) }) : t("localTime.opensIn", { wait: duration(call.minutes, t) })}
        </span>
      </span>
    </div>
  );
}
